
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Ticket,
  FileSpreadsheet,
  Settings,
  LogOut,
  ShoppingCart,
  Moon,
  Sun,
  Languages,
  Calculator,
  Warehouse,
  ShieldCheck,
  Users,
  Award,
  Receipt,
  BellRing,
  Shield,
  TrendingUp,
  Truck,
  HeartHandshake,
  UserCog,
  Clock,
  Banknote,
  Factory,
  Wrench,
  GraduationCap,
  Package,
  Cog,
  ChevronDown,
  ChevronLeft,
} from 'lucide-react';

interface NavItem {
  path: string;
  labelKey: string;
  icon: any;
  group?: string;
}

const navItems: NavItem[] = [
  // ─── Main ───
  { path: '/', labelKey: 'nav_dashboard', icon: LayoutDashboard },
  // ─── Sales ───
  { path: '/sales', labelKey: 'nav_sales', icon: TrendingUp, group: 'sales' },
  { path: '/pos', labelKey: 'nav_pos', icon: ShoppingCart, group: 'sales' },
  // ─── CRM ───
  { path: '/crm', labelKey: 'nav_crm', icon: HeartHandshake, group: 'crm' },
  { path: '/contacts', labelKey: 'nav_contacts', icon: Users, group: 'crm' },
  // ─── Purchases ───
  { path: '/purchases', labelKey: 'nav_purchases', icon: Truck, group: 'purchases' },
  // ─── Inventory ───
  { path: '/inventory', labelKey: 'nav_inventory', icon: Warehouse, group: 'inventory' },
  { path: '/product-tracking', labelKey: 'nav_product_tracking', icon: Shield, group: 'inventory' },
  { path: '/stock-notifications', labelKey: 'nav_stock_notifications', icon: BellRing, group: 'inventory' },
  { path: '/products', labelKey: 'nav_products', icon: Package, group: 'inventory' },
  { path: '/warehouse', labelKey: 'nav_warehouse', icon: Warehouse, group: 'inventory' },
  { path: '/spare-parts', labelKey: 'nav_spare_parts', icon: Cog, group: 'inventory' },
  // ─── Service ───
  { path: '/tickets', labelKey: 'nav_tickets', icon: Ticket, group: 'service' },
  { path: '/field-service', labelKey: 'nav_field_service', icon: Wrench, group: 'service' },
  // ─── HR ───
  { path: '/employees', labelKey: 'nav_employees', icon: UserCog, group: 'hr' },
  { path: '/attendance', labelKey: 'nav_attendance', icon: Clock, group: 'hr' },
  { path: '/payroll', labelKey: 'nav_payroll', icon: Banknote, group: 'hr' },
  { path: '/training', labelKey: 'nav_training', icon: GraduationCap, group: 'hr' },
  // ─── Manufacturing ───
  { path: '/manufacturing', labelKey: 'nav_manufacturing', icon: Factory, group: 'manufacturing' },
  // ─── Finance ───
  { path: '/invoices', labelKey: 'nav_invoices', icon: Receipt, group: 'finance' },
  { path: '/accounting', labelKey: 'nav_accounting', icon: Calculator, group: 'finance' },
  // ─── Admin ───
  { path: '/approvals', labelKey: 'nav_approvals', icon: ShieldCheck, group: 'admin' },
  { path: '/performance', labelKey: 'nav_performance', icon: Award, group: 'admin' },
  { path: '/import', labelKey: 'nav_import', icon: FileSpreadsheet, group: 'admin' },
  { path: '/settings', labelKey: 'nav_settings', icon: Settings, group: 'admin' },
];

const NAV_GROUPS: Record<string, string> = {
  sales: 'المبيعات',
  crm: 'علاقات العملاء',
  purchases: 'المشتريات',
  inventory: 'المخزون',
  service: 'الخدمة',
  hr: 'الموارد البشرية',
  manufacturing: 'التصنيع',
  finance: 'المالية',
  admin: 'الإدارة',
};
import { useUiStore } from '@/stores/ui-store';

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, dir } = useI18n();
  const { logout, session } = useAuthStore();
  const { isSidebarOpen, setSidebarOpen } = useUiStore();
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(new Set());

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  // Group nav items
  const dashboardItem = navItems.find(i => i.path === '/')!;
  const groupedItems = navItems.filter(i => i.path !== '/');
  const groups: Record<string, NavItem[]> = {};
  for (const item of groupedItems) {
    const g = item.group || 'other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(item);
  }

  return (
    <aside className={cn(
      "w-64 h-screen bg-card flex flex-col fixed top-0 transition-transform duration-300 z-30",
      dir === 'rtl' ? 'right-0 border-l' : 'left-0 border-r',
      !isSidebarOpen && (dir === 'rtl' ? 'translate-x-full md:translate-x-0' : '-translate-x-full md:translate-x-0')
    )}>
      {/* Logo */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-md">
            <LayoutDashboard className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-tight">MR | CRM</h1>
            <p className="text-xs text-muted-foreground">نظام إدارة متكامل</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {/* Dashboard */}
        {(() => {
          const Icon = dashboardItem.icon;
          const isActive = location.pathname === '/';
          return (
            <button
              onClick={() => { navigate('/'); setSidebarOpen(false); }}
              className={cn('sidebar-link w-full', isActive && 'active')}
            >
              <Icon className="w-5 h-5" />
              <span>{t(dashboardItem.labelKey as any)}</span>
            </button>
          );
        })()}

        {/* Grouped Items */}
        {Object.entries(groups).map(([groupKey, items]) => {
          const isCollapsed = collapsedGroups.has(groupKey);
          const hasActive = items.some(item =>
            location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
          );

          return (
            <div key={groupKey} className="mt-1">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors rounded-md',
                  hasActive && 'text-primary'
                )}
              >
                <span>{NAV_GROUPS[groupKey] || groupKey}</span>
                {isCollapsed ?
                  <ChevronLeft className={cn('w-3 h-3', dir === 'rtl' && 'rotate-180')} /> :
                  <ChevronDown className="w-3 h-3" />
                }
              </button>

              {/* Group Items */}
              {!isCollapsed && items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path));

                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={cn('sidebar-link w-full text-sm', isActive && 'active')}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{t(item.labelKey as any)}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-3 border-t space-y-2">
        {session && (
          <div className="px-4 py-2 text-xs text-muted-foreground truncate">
            {session.name}
          </div>
        )}
        <button
          onClick={logout}
          className="sidebar-link w-full text-destructive hover:bg-destructive/10"
        >
          <LogOut className="w-5 h-5" />
          <span>{t('logout')}</span>
        </button>
      </div>
    </aside>
  );
};
